import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

const inputStyle = 'bg-white/5 border-white/15 text-white placeholder:text-slate-500';
export const lines = (value) => Array.isArray(value) ? value.join('\n') : (value || '');
export const responsibilityItems = (value) => [...new Set([...(value.responsibilities || []), ...(value.achievements || [])])];

export function CvField({ path, label, value, onChange, multiline = false, list = false, rows = 3, includes, ...props }) {
    const id = `cv-${path.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    // A source cell may contain line breaks even in an otherwise short field.
    // Text inputs silently strip them; use a textarea to preserve the value.
    const Control = multiline || list || (typeof value === 'string' && /[\r\n]/.test(value)) ? Textarea : Input;
    return <div className="space-y-2">
        <Label htmlFor={id} className="text-sm text-slate-300">{label}</Label>
        <Control id={id} data-cv-path={path} data-cv-includes={includes?.join(',')}
            value={list ? lines(value) : (value ?? '')} rows={rows}
            onChange={event => onChange(list ? event.target.value.split('\n') : event.target.value)}
            className={inputStyle} {...props} />
    </div>;
}

export function CvSectionsEditor({ value = [], onChange, path }) {
    const update = (index, patch) => onChange(value.map((section, i) => i === index ? { ...section, ...patch } : section));
    return <div className="space-y-4">
        {value.map((section, index) => <fieldset key={index} className="space-y-3 rounded-lg border border-white/10 p-4">
            <legend className="px-2 text-sm text-slate-300">Rubrique {index + 1}</legend>
            <CvField path={`${path}.${index}.title`} label="Titre de la rubrique" value={section.title}
                onChange={title => update(index, { title })} />
            <CvField path={`${path}.${index}.content`} label="Contenu de la rubrique" value={section.content} multiline
                onChange={content => update(index, { content })} />
            <CvField path={`${path}.${index}.items`} label="Éléments de la rubrique — un par ligne" value={section.items} list
                onChange={items => update(index, { items })} />
            <Button type="button" variant="ghost" onClick={() => onChange(value.filter((_, i) => i !== index))}
                aria-label={`Retirer la rubrique ${index + 1}`}>Retirer cette rubrique</Button>
        </fieldset>)}
        <Button type="button" variant="outline" onClick={() => onChange([...value, { title: '', content: '', items: [] }])}>
            Ajouter une rubrique
        </Button>
    </div>;
}

export function CvResponsibilityField({ value, onChange, path, shared = false }) {
    return <CvField path={`${path}.responsibilities`} includes={[`${path}.achievements`]}
        label={shared ? 'Rôle & Responsabilités communs' : 'Rôle & Responsabilités'}
        value={responsibilityItems(value)} list rows={5}
        onChange={responsibilities => onChange({ responsibilities, achievements: [] })} />;
}

export function CvScopeExtras({ value, onChange, path, group = false }) {
    const populated = Boolean(value.functional_environment?.length || value.urls?.length || value.sections?.length);
    return <details open={populated || undefined} className="rounded-lg border border-white/10 p-4">
        <summary className="cursor-pointer text-sm font-medium text-slate-300">Périmètre, liens et rubriques{group ? ' communs' : ''}</summary>
        <div className="mt-4 space-y-4">
            <CvField path={`${path}.functional_environment`} label="Environnement fonctionnel" value={value.functional_environment} list
                onChange={functional_environment => onChange({ functional_environment })} />
            <CvField path={`${path}.urls`} label="Liens — un par ligne" value={value.urls} list
                onChange={urls => onChange({ urls })} />
            <CvSectionsEditor path={`${path}.sections`} value={value.sections} onChange={sections => onChange({ sections })} />
        </div>
    </details>;
}

export function CvProjectIdentity({ value, onChange, path, group = false }) {
    return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <CvField path={`${path}.client`} label={group ? 'Client de l’équipe, si indiqué' : 'Client propre au projet, si indiqué'} value={value.client}
            onChange={client => onChange({ client })} />
        <CvField path={`${path}.role`} label={group ? 'Rôle dans l’équipe, si indiqué' : 'Rôle propre au projet, si indiqué'} value={value.role}
            onChange={role => onChange({ role })} />
        <CvField path={`${path}.start_date`} label="Date de début, si indiquée" value={value.start_date}
            placeholder="2022 ou 03/2022" onChange={start_date => onChange({ start_date })} />
        <CvField path={`${path}.end_date`} label="Date de fin, si indiquée" value={value.end_date}
            placeholder="2024 ou En cours" onChange={end_date => onChange({ end_date })} />
        <label className="flex min-h-11 items-center gap-3 text-sm text-slate-300">
            <input type="checkbox" checked={Boolean(value.is_current)} data-cv-path={`${path}.is_current`}
                onChange={event => onChange({ is_current: event.target.checked })} />
            {group ? 'Équipe actuelle' : 'Projet en cours'}
        </label>
    </div>;
}
